import api from '../httpClient';
import { courseRuntimeUrl } from './courseRuntimeContext';

export interface QuestionGenerationState {
  status: 'not_started' | 'generating' | 'ready';
  generation_id: number | null;
  question_text: string | null;
  retry_required: boolean;
  restart_required: boolean;
}

export const isQuestionGenerationConflict = (caught: unknown): boolean => {
  const data = (caught as { response?: { data?: { generation_id?: unknown; detail?: unknown; code?: string } } })?.response?.data;
  const detail = Array.isArray(data?.detail) ? data?.detail.filter((value): value is string => typeof value === 'string').join(' ') : data?.detail;
  return Boolean(data?.generation_id || data?.code === 'question_generation_stale'
    || (typeof detail === 'string' && /personalized|generated question|generation|read.only.*flow run/i.test(detail)));
};

const endpoint = (flowName: string, flowItemId: number) =>
  courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/question-generation/${flowItemId}/`);

// StrictMode and overlapping mounts share work, while the server remains the
// durable cache. Never prefetch: the previous answer must be saved first.
const pending = new Map<string, Promise<QuestionGenerationState>>();
export const questionGeneration = {
  newAttempt: (flowName: string, flowItemId: number, flowRunId: number, generationId?: number) =>
    api.post<{ flow_run_id: number }>(endpoint(flowName, flowItemId), { flow_run: flowRunId, action: 'new_attempt', ...(generationId ? { generation_id: generationId } : {}) }).then(({ data }) => data),
  ensure: (flowName: string, flowItemId: number, flowRunId: number) => {
    const url = endpoint(flowName, flowItemId);
    const key = `${url}:${flowRunId}`;
    const existing = pending.get(key);
    if (existing) return existing;
    const request = api.post<QuestionGenerationState>(url, { flow_run: flowRunId })
      .then(({ data }) => data).finally(() => pending.delete(key));
    pending.set(key, request);
    return request;
  },
  get: (flowName: string, flowItemId: number, flowRunId: number) =>
    api.get<QuestionGenerationState>(endpoint(flowName, flowItemId), { params: { flow_run: flowRunId } }).then(({ data }) => data),
};
